import React, { useContext, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Checkbox } from '../../Checkbox';
import { DropdownContext } from '../context';
import { DropdownTypes, filterDisabledItems, generateItemKey, filterOutItems } from '../helper';
import styles from '@dtsl/css-dropdown/dist/index.module.css';
import ChevronUp from '@dtsl/icons/dist/icons/react/ChevronUp';
import { onCollapsable } from '@dtsl/react/dist/components/Utils/collapsible';
import '@dtsl/css-dropdown/dist/collapsible.css';
function CollapsableList(props) {
  var _collapsibleDropdownC, _collapsibleDropdownC2;
  const {
    heading,
    children,
    fireChangeEvent,
    disabled,
    dropdownType,
    items,
    index
  } = props;
  const collapsibleDropdownControlRef = useRef(null);
  const {
    selectedValues,
    setSelectedValues
  } = useContext(DropdownContext);
  const selectedValueseExcludingFolderItems = filterOutItems(selectedValues, items);
  const totalSelectedItemsInFolder = selectedValues.length - selectedValueseExcludingFolderItems.length;
  const selectableFolderItems = filterDisabledItems(items.map((item, indx) => ({
    ...item,
    key: (item === null || item === void 0 ? void 0 : item.key) || generateItemKey(index, indx)
  })));
  const selectAllFolderItems = () => {
    if (isChecked) {
      setSelectedValues([...selectedValueseExcludingFolderItems]);
      fireChangeEvent([...selectedValueseExcludingFolderItems]);
    } else {
      setSelectedValues([...selectableFolderItems, ...selectedValueseExcludingFolderItems]);
      fireChangeEvent([...selectableFolderItems, ...selectedValueseExcludingFolderItems]);
    }
  };
  const handleClick = event => {
    if (dropdownType === DropdownTypes.MULTI_SELECTION) {
      var _event$target, _event$target$parentE, _event$target$parentE2;
      if ((_event$target = event.target) !== null && _event$target !== void 0 && (_event$target$parentE = _event$target.parentElement) !== null && _event$target$parentE !== void 0 && (_event$target$parentE2 = _event$target$parentE.parentElement) !== null && _event$target$parentE2 !== void 0 && _event$target$parentE2.classList.contains(styles['sib-dropdown-select-all-folders'])) {
        if (event.target.parentElement.classList.contains(styles['sib-checkbox-label']) || event.target.parentElement.classList.contains(styles['sib-checkbox-wrapper'])) {
          selectAllFolderItems();
        } else {
          onCollapsable(event);
        }
      } else {
        onCollapsable(event);
      }
    } else {
      onCollapsable(event);
    }
  };
  const handlePropagation = event => {
    event.stopPropagation();
  };
  const handleKeyDown = event => {
    if (event.which === 13 || event.which === 32) {
      event.stopPropagation();
      selectAllFolderItems();
    }
  };
  const groupClass = classNames({
    'sib-collapsible': true,
    [styles['sib-dropdown-option-group']]: true,
    disabled: disabled
  });
  const onKeyDown = event => {
    if (event.key === 'Enter') {
      onCollapsable(event);
    }
  };
  const onMouseOver = event => {
    event.stopPropagation();
    event.preventDefault();
    const dropdownWrapper = event.target.closest('#sib-dropdown-id');
    Array.from(dropdownWrapper.getElementsByClassName(styles['sib-dropdown-option-group'])).forEach(parentNode => {
      Array.from(parentNode.querySelector('#sib-dropdown-option-group-content-id').children).forEach(node => {
        if (!node.classList.contains(styles['is-selected'])) {
          node.classList.remove(styles['is-hovered']);
        }
      });
    });
  };
  const variant = totalSelectedItemsInFolder === selectableFolderItems.length ? 'positive' : 'undetermined';
  const isChecked = totalSelectedItemsInFolder > 0;
  const folderHeading = isChecked => {
    if (dropdownType === DropdownTypes.MULTI_SELECTION) {
      return /*#__PURE__*/React.createElement(Checkbox, {
        label: `${heading} (${totalSelectedItemsInFolder}/${selectableFolderItems.length})`,
        checked: isChecked ? true : false,
        identifier: `sibCheckbox_${heading}`,
        className: `${styles['sib-dropdown-select-all-folders']} ${styles['sib-checkbox-wrapper']}`,
        name: "sib-dropdown-group-checkbox",
        variant: variant,
        onChange: handlePropagation,
        labelContainerClassName: `${styles['sib-checkbox-title']} ${styles['sib-checkbox-label']}`,
        onClick: handlePropagation,
        onKeyDown: handleKeyDown
      });
    } else {
      return /*#__PURE__*/React.createElement("span", {
        className: styles['sib-dropdown-option-group-title']
      }, heading);
    }
  };
  const collapsibleContentClasses = classNames({
    'sib-collapsible__content': true,
    'sib-collapsible__content_toggled': true,
    [styles['sib-dropdown-option-group-content']]: true
  });
  const collapsibleTriggerClasses = classNames({
    'sib-collapsible__trigger': true,
    'sib-collapsible__trigger_toggled': true,
    [styles['sib-dropdown-option-group-header']]: true
  });
  return /*#__PURE__*/React.createElement("li", {
    role: "presentation",
    className: groupClass,
    onClick: handleClick,
    onFocus: onMouseOver
  }, /*#__PURE__*/React.createElement("div", {
    ref: collapsibleDropdownControlRef,
    role: "button",
    tabIndex: "0",
    className: collapsibleTriggerClasses,
    disabled: disabled,
    "aria-expanded": (_collapsibleDropdownC = collapsibleDropdownControlRef.current) !== null && _collapsibleDropdownC !== void 0 && (_collapsibleDropdownC2 = _collapsibleDropdownC.classList) !== null && _collapsibleDropdownC2 !== void 0 && _collapsibleDropdownC2.contains('sib-collapsible__trigger_toggled') ? true : false,
    onKeyDown: onKeyDown
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-dropdown-option-group-header-content']
  }, folderHeading(isChecked), /*#__PURE__*/React.createElement(ChevronUp, {
    className: 'sib-dropdown-option-group-marker'
  }))), /*#__PURE__*/React.createElement("ul", {
    className: collapsibleContentClasses,
    "data-collapsible-behavior": "shutter",
    "data-collapse-speed": "120",
    id: "sib-dropdown-option-group-content-id"
  }, children));
}
CollapsableList.defaultProps = {
  heading: '',
  disabled: false,
  items: [],
  dropdownType: DropdownTypes.SINGLE_SELECTION,
  index: 1,
  fireChangeEvent: () => {}
};
CollapsableList.propTypes = {
  heading: PropTypes.string,
  items: PropTypes.array,
  disabled: PropTypes.bool,
  dropdownType: PropTypes.string,
  children: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  fireChangeEvent: PropTypes.func.isRequired
};
export default CollapsableList;